import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LinkIcon from "@mui/icons-material/Link";
import CodeIcon from "@mui/icons-material/Code";
import EngineeringIcon from "@mui/icons-material/Engineering";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import StarIcon from "@mui/icons-material/Star";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AiMachineIntelligence from "../components/Cv/AiMachineIntelligence";
import Certifications from "../components/Cv/Certifications";
import CvBuilderMenu from "../components/CvBuilderMenu";
import CVHeader from "../components/CvBuilderMenu/CVHeader";
import Courses from "../components/Cv/Courses";
import Education from "../components/Cv/Education";
import Expertise from "../components/Cv/AreasOfExpertise";
import Languages from "../components/Cv/Languages";
import Methods from "../components/Cv/Methods";
import Portfolio from "../components/Cv/Portfolio";
import Projects from "../components/Cv/Projects";
import Roles from "../components/Cv/MainRoles";
import Summary from "../components/Cv/Summary";
import TokenExpiredModal from "../components/TokenExpiredModal";
import WorkExperiences from "../components/Cv/WorkExperiences";
import api from "../api/axiosInstance";
import { primaryRoles } from "config";
import { usePatchCvMutation } from "../lib/cvsSlice";
import { CvStepNavigationProvider } from "../contexts/CvStepNavigationContext";
import { toast } from "react-toastify";

/* ─── Role badge styled components ──────────────────────────── */

const RoleBadgeWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: theme.spacing(0.5),
}));

const RoleBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  backgroundColor: theme.palette.background.subtle,
  borderRadius: theme.shape.pill,
  padding: theme.spacing(1, 2),
  cursor: "pointer",
  "&:hover": { backgroundColor: theme.palette.background.neutral },
}));

const RoleText = styled(Typography)(({ theme }) => ({
  ...theme.typography.md,
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
}));

/* ─── Helper ─────────────────────────────────────────────────── */

const hasItems = (v) => Array.isArray(v) && v.length > 0;

const CvDetails = ({ mainroles }) => {
  let { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cvDetails, setCvDetails] = useState(null);
  const [tokenExpiredModalOpen, setTokenExpiredModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRole, setSelectedRole] = useState(mainroles);

  const [patchCv] = usePatchCvMutation();
  const [activeStep, setActiveStep] = React.useState(0);

  const fetchCvAndCandidate = async (id) => {
    try {
      const { data: cvResponse } = await api.get(`/cv/${id}`);

      setCvDetails(cvResponse);
      setLoading(false);
    } catch (error) {
      console.log(error);
      if (error.response?.status === 401) {
        setTokenExpiredModalOpen(true);
      }
      setLoading(true);
    }
  };

  useEffect(() => {
    fetchCvAndCandidate(id);
    const refreshInterval = setInterval(() => fetchCvAndCandidate(id), 1000);
    return () => clearInterval(refreshInterval);
  }, [id, mainroles]);

  const initialStepSetForCv = React.useRef(null);
  useEffect(() => {
    if (cvDetails && !loading && initialStepSetForCv.current !== id) {
      initialStepSetForCv.current = id;
      setActiveStep(0); // Always open on Summary section
    }
  }, [id, cvDetails, loading]);

  const handleEditClick = () => {
    setSelectedRole(cvDetails.mainRole);
    setEditMode(true);
  };

  const handleSaveClick = async () => {
    try {
      await patchCv({ cvId: id, mainroles: selectedRole }).unwrap();
      setEditMode(false);
    } catch (error) {
      toast.error(`An error occurred while updating mainrole: ${error}`);
    }
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  if (loading) {
    return (
      <>
        <TokenExpiredModal
          isOpen={tokenExpiredModalOpen}
          onClose={() => setTokenExpiredModalOpen(false)}
        />
        <Paper
          elevation={1}
          sx={{
            minHeight: "100vh",
            justifyContent: "center",
            alignItems: "center",
            p: 1,
          }}
        >
          <CircularProgress />
        </Paper>
      </>
    );
  }

  if (!cvDetails) {
    return null;
  }

  const rolesInProject = (cvDetails.projects || []).map((project) => project.roleInProject).flat();
  const {
    _id: cvId,
    summary,
    projects,
    experiences,
    careerBreak,
    portfolio,
    links,
    expertise,
    roles,
    certifications,
    courses,
    languages,
    methods,
    educations,
    aiEngineering,
    aiEnabledWork,
  } = cvDetails;

  const steps = [
    {
      label: "Summary",
      helperText: "Professional Overview",
      icon: <PersonOutlineIcon />,
      tooltipText:
        "Your first impression — highlight key skills, top assignments, and your professional profile.",
      popupText: `This section is critical. Clients often decide within 10–15 seconds whether to continue reading.

Combine into one clear and easy-to-read section:

Key skills relevant to the type of assignment you want

Two or three top assignments (1–2 sentences each). These should match your Highlighted Roles.

A short professional summary — who you are, your strengths, your mindset, and the value you deliver

Be concise and concrete. Combine hard and soft skills. Avoid exaggerations, generic phrases, and superlatives. Focus on clarity, relevance, and credibility.`,
      isDataFilled: Boolean(summary && typeof summary === "string" && summary.trim() && summary !== "No summary provided."),
      content: <Summary summary={summary || "No summary provided."} cvId={cvId} />,
    },
    {
      label: "Assignments & Projects",
      helperText: "Key Contributions & Initiatives",
      icon: <CodeIcon />,
      tooltipText:
        "Focus on relevant, recent projects, your role, and measurable results.",
      popupText: `Describe assignments where you made a real contribution.

For each assignment:

Provide context (industry, business objective, technical environment)

Specify your role and level of responsibility

Clearly describe what you did — not just what the team did

Highlight measurable outcomes (efficiency gains, cost reduction, delivery milestones, performance improvements)

Prioritize recent and relevant projects. Avoid outdated or unrelated experience unless it clearly strengthens your positioning.

Think of the reader as evaluating your CV against a checklist. Make it easy for them to confirm you match the requirements.`,
      isDataFilled: hasItems(projects),
      content: <Projects key={4} projs={projects} cvId={cvId} />,
    },
    {
      label: "Work Experience",
      helperText: "Employment History",
      icon: <EngineeringIcon />,
      tooltipText:
        "List your employment history with role, scope, and responsibility level.",
      popupText: `Provide a structured overview of your employment history.

Include:

Company name

Title

Employment period

Short description of responsibilities and scope

Focus on clarity rather than long narratives. This section provides context for your assignments and professional development.`,
      isDataFilled: hasItems(experiences),
      content: <WorkExperiences experiences={experiences} careerBreak={careerBreak} cvId={cvId} />,
    },
    {
      label: "Professional Links",
      helperText: "Work Samples & Online Profiles",
      icon: <LinkIcon />,
      tooltipText:
        "Ensure LinkedIn and online profiles match your Consultant CV.",
      popupText: `Clients often check LinkedIn or search your name online.

Make sure your online presence:

Is up to date

Matches your Consultant CV

Reflects your current role and skills

Projects a professional image aligned with client expectations

Be mindful of public content. Consistency builds credibility.`,
      isDataFilled: Boolean(portfolio) || hasItems(links),
      content: <Portfolio portfolio={portfolio} links={links} cvId={cvId} />,
    },
    {
      label: "Area of Expertise",
      helperText: "Key Skills and Specializations",
      icon: <StarBorderIcon />,
      tooltipText:
        "Provide a structured list of key skills grouped by category with proficiency levels.",
      popupText: `Build a clear and structured skills inventory.

Group skills logically (e.g., Backend, Cloud, Data, Frontend, Architecture, Leadership)

Prioritize by relevance

Include proficiency levels

Focus on skills that are in demand and aligned with the assignments you target

Avoid long, unstructured keyword lists. Clarity improves readability and searchability.`,
      isDataFilled: Boolean(expertise) && (Array.isArray(expertise) ? expertise.length > 0 : hasItems(expertise?.expertise)),
      content: <Expertise expertise={expertise} cvId={cvId} label="Areas of Expertise" />,
    },
    {
      label: "Highlighted Roles",
      helperText: "Notable work experiences where I feel I add great value",
      icon: <WorkOutlineIcon />,
      tooltipText:
        "Select your most relevant and high-impact assignments.",
      popupText: `Highlighted Roles are your strongest and most representative assignments.

These should:

Be highly relevant to the type of assignments you want

Demonstrate responsibility, ownership, and impact

Support the summary section

Choose quality over quantity. Two to four strong highlighted roles are typically sufficient.`,
      isDataFilled: hasItems(roles),
      content: <Roles roles={roles} cvId={cvId} />,
    },
    {
      label: "AI & Machine Intelligence",
      helperText: "AI Expertise & Skills",
      icon: <PsychologyIcon />,
      tooltipText:
        "Describe your AI-related expertise, tools, and applied use cases.",
      popupText: `Include AI-related competencies if relevant.

You may include:

AI engineering and integration experience

LLM usage, prompt engineering, agent-based workflows

Machine learning frameworks or MLOps

AI-enabled development workflows

Applied AI use cases and measurable outcomes

Specify your level of involvement and responsibility. Distinguish between hands-on implementation and end-user usage.

Only include AI expertise if it reflects real competence and experience.`,
      isDataFilled: hasItems(aiEngineering) || hasItems(aiEnabledWork),
      content: (
        <AiMachineIntelligence
          aiEngineering={aiEngineering || []}
          aiEnabledWork={aiEnabledWork || []}
          cvId={cvId}
        />
      ),
    },
    {
      label: "License and Certificates",
      helperText: "Credentials & Qualifications",
      icon: <StarIcon />,
      tooltipText: "List relevant and valid certifications.",
      popupText: `Include certifications that strengthen your qualification for assignments.

For each certification:

Provide official name

Level (if applicable)

Issuing organization

Date (and expiration if relevant)

Many industries require specific certifications to qualify for projects. Accuracy and clarity are important.`,
      isDataFilled: hasItems(certifications),
      content: <Certifications certs={certifications} cvId={cvId} />,
    },
    {
      label: "Courses",
      helperText: "Training & Professional Development",
      icon: <StarIcon />,
      tooltipText:
        "List relevant courses that strengthen your consulting profile.",
      popupText: `Include courses that add value in consulting contexts.

Focus on:

Technical upskilling

Leadership development

Specialized domain knowledge

Avoid listing minor or unrelated courses unless they support your professional direction.`,
      isDataFilled: hasItems(courses),
      content: <Courses courses={courses} cvId={cvId} />,
    },
    {
      label: "Languages",
      helperText: "Language Proficiency",
      icon: <StarIcon />,
      tooltipText:
        "Include only languages relevant for consulting assignments and only for languages on intermediate level or above.",
      popupText: `List languages that are relevant for client work.

Include:

Language

Proficiency level

Be realistic in your assessment. Clients may validate language proficiency during interviews.`,
      isDataFilled: hasItems(languages),
      content: <Languages langs={languages} cvId={cvId} />,
    },
    {
      label: "Methods",
      helperText: "Techniques & Methodologies",
      icon: <StarIcon />,
      tooltipText:
        "Provide a structured list of tools, frameworks, and methodologies with proficiency levels.",
      popupText: `Build a structured tools and methodologies inventory.

Group logically (e.g., Agile, DevOps, ITIL, SAFe, Scrum, Design Thinking)

Include proficiency levels

Focus on frameworks relevant to consulting assignments

Distinguish between certified knowledge and practical experience.`,
      isDataFilled: hasItems(methods),
      content: <Methods method={methods} cvId={cvId} />,
    },
    {
      label: "Education",
      helperText: "Academic Background",
      icon: <StarIcon />,
      tooltipText: "List higher education (college level and above).",
      popupText: `Include formal higher education.

Provide:

Degree

Field of study

Institution

Graduation year

Only include education that supports your professional positioning.`,
      isDataFilled: hasItems(educations),
      content: <Education educations={educations} cvId={cvId} />,
    },
  ];

  return (
    <>
      <TokenExpiredModal
        isOpen={tokenExpiredModalOpen}
        onClose={() => setTokenExpiredModalOpen(false)}
      />

      {!loading && (
        <>
          <CVHeader
            menuItems={
              <RoleBadgeWrapper>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {editMode ? (
                    <>
                      <Select
                        value={selectedRole}
                        onChange={handleRoleChange}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        {primaryRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                      <IconButton onClick={handleSaveClick} color="primary">
                        Save
                      </IconButton>
                    </>
                  ) : (
                    <RoleBadge onClick={handleEditClick}>
                      <RoleText>{cvDetails.mainRole}</RoleText>
                      <EditIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    </RoleBadge>
                  )}
                </Box>
                {rolesInProject?.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Other roles: {rolesInProject.join(", ")}
                  </Typography>
                )}
              </RoleBadgeWrapper>
            }
          />
          <CvStepNavigationProvider
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            totalSteps={steps.length}
            onFinish={() => navigate("/cvs")}
          >
            <CvBuilderMenu steps={steps} />
          </CvStepNavigationProvider>
        </>
      )}
    </>
  );
};

export default CvDetails;
